import { parseManifest, type ThemeManifest } from './manifest.js';
import type { MascotSlot, SceneKey, ThemePack, ThemeRegistry } from './types.js';

export interface ThemePackSource {
  /** Raw manifest object (parsed from `theme-pack.json`). */
  manifestRaw: unknown;
  /**
   * Resolved asset URL map: relative path inside the pack → final URL.
   * Vite's `import.meta.glob({ as: 'url', eager: true })` produces this shape.
   */
  assets: Record<string, string>;
}

export class BundledThemeRegistry implements ThemeRegistry {
  private readonly packs = new Map<string, ThemePack>();

  constructor(sources: ThemePackSource[]) {
    for (const source of sources) {
      const manifest = parseManifest(source.manifestRaw);
      const pack = this.buildPack(manifest, source.assets);
      this.packs.set(manifest.key, pack);
    }
  }

  list(): ThemeManifest[] {
    return Array.from(this.packs.values()).map((p) => p.manifest);
  }

  get(key: string): ThemePack | undefined {
    return this.packs.get(key);
  }

  private buildPack(manifest: ThemeManifest, assets: Record<string, string>): ThemePack {
    const resolveAsset = (relative: string): string => {
      const url = assets[relative];
      if (!url) {
        throw new Error(
          `[theme-sdk] Pack "${manifest.key}" references missing asset "${relative}". Available: ${Object.keys(assets).join(', ')}`,
        );
      }
      return url;
    };

    const homeBg = resolveAsset(manifest.backgrounds.home);
    const homeMascot = resolveAsset(manifest.mascot.home);

    return {
      manifest,
      getBackground(scene: SceneKey): string {
        const slot = manifest.backgrounds[scene];
        return slot ? resolveAsset(slot) : homeBg;
      },
      getMascot(slot: MascotSlot): string {
        if (slot === 'home') return homeMascot;
        if (slot === 'mm' && manifest.mascot.mm) return resolveAsset(manifest.mascot.mm);
        return homeMascot;
      },
    };
  }
}
